import type { AttendanceStatus, TimetableBlock } from '@classgo/shared';

const STATUSES: readonly AttendanceStatus[] = ['present', 'absent', 'late', 'excused'];

/**
 * The next mark in the tap cycle, the same one the app's session screen uses (#150):
 * present → absent → late → excused → present.
 *
 * @param {AttendanceStatus} status -- The mark shown.
 * @returns The mark after one tap.
 */
export const nextStatus = (status: AttendanceStatus): AttendanceStatus =>
  STATUSES[(STATUSES.indexOf(status) + 1) % STATUSES.length] ?? 'present';

/**
 * The weekday of a `YYYY-MM-DD` date as the timetable counts it: 1 = lunes … 7 = domingo.
 *
 * @param {string} date -- The date.
 * @returns The weekday.
 */
export const weekdayOf = (date: string): number => {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
};

/**
 * The sessions of a group on a date: its timetable blocks of that weekday, earliest first.
 *
 * @param {readonly TimetableBlock[]} blocks -- The group's week.
 * @param {string} date -- The date.
 * @returns The blocks of that weekday.
 */
export const sessionsOn = (blocks: readonly TimetableBlock[], date: string): TimetableBlock[] =>
  blocks
    .filter((block) => block.weekday === weekdayOf(date))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

/**
 * How many students hold each mark.
 *
 * @param {Record<string, AttendanceStatus>} marks -- The marks by student.
 * @returns The counts.
 */
export const countMarks = (
  marks: Record<string, AttendanceStatus>,
): Record<AttendanceStatus, number> => {
  const counts: Record<AttendanceStatus, number> = { present: 0, absent: 0, late: 0, excused: 0 };
  for (const status of Object.values(marks)) counts[status] += 1;
  return counts;
};

/**
 * A student's name as the roll shows it, family name first.
 *
 * @param {object} row -- The names.
 * @returns The name.
 */
export const rollName = (row: { givenName: string | null; familyName: string | null }): string =>
  [row.familyName, row.givenName].filter(Boolean).join(', ');
